/**
 * Rendre lisible une erreur venue de la base.
 *
 * Ce fichier existe à cause d'un défaut réel, et coûteux : l'écran affichait
 * « Recherche impossible » sur **tous** les paiements, en masquant la vraie
 * cause — une requête refusée par PostgREST. Le motif fautif était partout :
 *
 * ```ts
 * e instanceof Error ? e.message : 'Recherche impossible'
 * ```
 *
 * Une erreur Supabase n'est **pas** une instance de `Error` : c'est un objet
 * ordinaire `{ message, details, hint, code }`. Le test échouait donc toujours,
 * et le message de repli — écrit pour un cas improbable — devenait le seul
 * message jamais affiché. Un diagnostic rendu invisible par la façon de
 * l'afficher.
 *
 * D'où cette fonction, à utiliser partout où une erreur remonte à l'écran.
 */

/** Forme d'une erreur PostgREST, telle que `@supabase/supabase-js` la rend. */
interface ErreurPostgrest {
  message?: unknown;
  details?: unknown;
  hint?: unknown;
  code?: unknown;
}

function texte(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * Le message le plus informatif qu'on puisse tirer d'une erreur.
 *
 * `details` et `hint` sont conservés parce que ce sont eux qui portent
 * l'information utile : le code `PGRST201` dit seulement « relation ambiguë »,
 * son `details` nomme les deux clés étrangères en cause.
 */
export function messageErreur(e: unknown, defaut = 'Opération impossible'): string {
  if (e instanceof Error && e.message) return e.message;
  if (typeof e === 'string') return texte(e) ?? defaut;

  if (e && typeof e === 'object') {
    const o = e as ErreurPostgrest;
    const morceaux = [texte(o.message), texte(o.details), texte(o.hint)].filter(Boolean);
    const code = texte(o.code);
    if (morceaux.length > 0) {
      return code ? `${morceaux.join(' — ')} (${code})` : morceaux.join(' — ');
    }
    if (code) return `Erreur ${code}`;
  }

  return defaut;
}

/**
 * Le message qu'une Edge Function a réellement renvoyé.
 *
 * ## Le défaut que cette fonction existe pour corriger
 *
 * Mesuré en production le 10 septembre 2026. L'émission d'une note de crédit
 * a affiché :
 *
 * > La note de crédit NC-26-00515 est enregistrée, mais son PDF n'a pas pu
 * > être produit (**Edge Function returned a non-2xx status code**).
 *
 * Cette phrase ne dit rien. Le journal, lui, disait `POST | 403` — un refus
 * d'autorisation, pas un plantage — et la fonction avait mis sa raison dans
 * le corps de la réponse : « Réservé à l'administration et à la famille
 * concernée », ou « Session expirée ou jeton invalide ». **Deux diagnostics
 * opposés, tous deux masqués par le même message générique.**
 *
 * `supabase.functions.invoke` lève une `FunctionsHttpError` qui ne porte que
 * ce texte-là ; le corps de la réponse vit dans `.context`, et personne ne le
 * lisait. C'est exactement le défaut que `messageErreur` corrige un cran plus
 * haut : un diagnostic rendu invisible par la façon de l'afficher.
 *
 * ## Pourquoi elle est asynchrone
 *
 * Lire un corps de réponse l'est. Le prix est un `await` chez l'appelant, et
 * il est mérité : la seule alternative serait de deviner.
 *
 * `clone()` avant lecture — un corps ne se lit qu'une fois, et la même erreur
 * peut passer par deux gestionnaires. Le statut n'est ajouté que si le corps
 * n'a rien donné : quand la fonction a écrit sa raison, la répéter en chiffres
 * n'apprend rien.
 */
export async function messageDeFonction(
  e: unknown,
  defaut = 'Appel de fonction impossible',
): Promise<string> {
  const contexte = (e as { context?: unknown } | null | undefined)?.context;

  if (contexte && typeof contexte === 'object' && 'json' in contexte) {
    const reponse = contexte as Response;
    try {
      const corps = await (typeof reponse.clone === 'function' ? reponse.clone() : reponse).json();
      if (corps && typeof corps === 'object') {
        const o = corps as ErreurPostgrest;
        const lu = texte((o as { error?: unknown }).error) ?? texte(o.message);
        if (lu) return lu;
      }
    } catch {
      /*
        Corps illisible : pas du JSON, déjà consommé, ou réponse vide. On
        retombe sur le message de l'erreur elle-même plutôt que d'inventer.
      */
    }
    const statut = typeof reponse.status === 'number' ? reponse.status : null;
    if (statut) return `${messageErreur(e, defaut)} (HTTP ${statut})`;
  }

  return messageErreur(e, defaut);
}
